import React, { useEffect } from "react";
import { useDropzone } from "react-dropzone";
import {
  CheckCircleFill,
  ExclamationCircleFill,
  X,
} from "react-bootstrap-icons";
import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from "@headlessui/react";
import { ChevronRightIcon } from "@heroicons/react/20/solid";
import { Button } from "@components";
import { cn } from "@utils/Cn";

import {
  FILE_STATUS,
  ACCEPT_MIME,
  bytesToSize,
  pickIcon,
  useFileManager,
} from "./fileUploaderUtils";




export default function FileUploader({
  onChange,
  defaultFiles = [],
  maxFiles = 8,
  maxSize = 4 * 1024 ** 2,
}) {
  const { items, validFiles, addFiles, removeFile, clearAll } = useFileManager({
    defaultFiles,
    maxFiles,
    maxSize,
  });

  const disabled = validFiles.length >= maxFiles;

  useEffect(() => onChange?.(validFiles), [validFiles, onChange]);

  /* Dropzone setup — react-dropzone filters UI via accept,
     but we still double-check in useFileManager for extra safety */
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: addFiles,
    multiple: true,
    disabled,
    accept: ACCEPT_MIME,
  });

  
  const renderStatusIcon = (status) =>
    status === FILE_STATUS.VALID ? (
      <CheckCircleFill className="text-green-700 w-3 h-3" />
    ) : (
      <ExclamationCircleFill className="text-red-600 w-3 h-3" />
    );

  const statusMsg = (status) => {
    switch (status) {
      case FILE_STATUS.TOO_LARGE:
        return "File is too large";
      case FILE_STATUS.OVER_LIMIT:
        return "Max files exceeded";
      case FILE_STATUS.BAD_TYPE:
        return "Unsupported file type";
      default:
        return null;
    }
  };

  

  return (
    <div className="space-y-2">
      {/* ─ Drop area (drag + browse) ─ */}
      <div
        {...getRootProps({
          className: cn(
            "group flex flex-col items-start justify-center p-4 border border-dashed rounded-lg transition-all border-zinc-300",
            disabled ? "cursor-default" : "cursor-pointer hover:border-zinc-400",
            isDragActive && !disabled && "border-primary-500 bg-primary-50"
          ),
        })}
      >
        <input {...getInputProps()} />
        <div className="flex justify-between items-start w-full">
          <div>
            <p className="font-medium text-zinc-900 text-sm">
              Drag and drop files here or upload
            </p>
            <p className="text-xs text-zinc-500">
              You can upload {maxFiles} files up to {bytesToSize(maxSize)} each
            </p>
            {disabled && (
              <p className="text-xs text-zinc-400 mt-2 group-hover:text-red-600">
                Maximum files uploaded
              </p>
            )}
          </div>
          <Button disabled={disabled} size="lg">Upload</Button>
        </div>
      </div>

      {/* ─ Collapsible list ─ */}
      {items.length > 0 && (
        <Disclosure as="div">
          <DisclosureButton className="group py-2 flex w-full items-center justify-between gap-3 text-left text-sm font-medium">
            <div className="flex items-center gap-3">
              <ChevronRightIcon
                className="size-5 shrink-0 text-zinc-400 group-data-[open]:rotate-90 group-data-[open]:text-zinc-500"
                aria-hidden
              />
              {renderStatusIcon(
                validFiles.length === items.length ? FILE_STATUS.VALID : null
              )}
              {validFiles.length} of {items.length} files ready to send
            </div>
            <button
              type="button"
              className="text-zinc-400 hover:text-zinc-500 mr-2 font-normal text-xs"
              onClick={clearAll}
            >
              Remove all
            </button>
          </DisclosureButton>

          <DisclosurePanel as="ul" className="flex flex-col">
            {items.map(({ id, file, status }) => {
              const Icon = pickIcon(file);
              return (
                <li
                  key={id}
                  className="flex items-start justify-between px-3 py-2 border-b border-zinc-200 last:border-b-0"
                >
                  <div className="flex items-start gap-3">
                    <Icon
                      className={cn(
                        "text-zinc-400 w-4 h-4 mt-0.5",
                        status !== FILE_STATUS.VALID && "opacity-50"
                      )}
                    />
                    <div>
                      <p
                        className={cn(
                          "font-medium text-sm text-zinc-900 truncate max-w-xs",
                          status !== FILE_STATUS.VALID && "opacity-50"
                        )}
                        title={file.name}
                      >
                        {file.name}
                      </p>
                      <p className="text-xs text-zinc-600 flex items-center gap-1">
                        {bytesToSize(file.size)}
                        {status !== FILE_STATUS.VALID && (
                          <span className="text-red-600 ml-2">
                            {statusMsg(status)}
                          </span>
                        )}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    {renderStatusIcon(status)}
                    <button
                      className="text-zinc-400 hover:text-zinc-600"
                      onClick={() => removeFile(id)}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </li>
              );
            })}
          </DisclosurePanel>
        </Disclosure>
      )}
    </div>
  );
}
